import Badge from '@components/Badge';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { GiftItemType, ReceivedGiftBoxRow } from '@models/gift';
import { useFlow } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import { formatGiftDate, getGiftDday } from '../../utils';

interface Props {
    gift: ReceivedGiftBoxRow;
}

function ReceivedGiftItem({ gift }: Props) {
    const { t } = useTranslation('gift');
    const { push } = useFlow();
    const dday = getGiftDday(gift.expiredAt);
    const isActive = !gift.status || gift.status === 'ISSUED';
    const subtitle = gift.issueType === 'EXTERNAL'
        ? t('giftBox.mobileVoucher')
        : t('giftBox.fromPrefix', { name: gift.senderName || '-' });

    const isUrgent = isActive && dday != null && dday >= 0 && dday <= 7;

    const statusLabel = (() => {
        if (isActive && dday != null && dday >= 0) return `D-${dday}`;
        if (isActive && dday != null && dday < 0) return t('giftBox.status.expired');
        switch (gift.status) {
        case 'EXPIRED':
            return t('giftBox.status.expired');
        case 'USED':
            return t('giftBox.status.used');
        case 'CANCELED':
            return t('giftBox.status.canceled');
        case 'DELETED':
            return t('giftBox.status.deleted');
        default:
            return null;
        }
    })();

    const handleClick = () => {
        push('GiftReceivedActivity', {
            giftBoxItemId: gift.id,
            giftItemType: GiftItemType.PRODUCT,
        });
    };

    return (
        <Item type='button'
            onClick={handleClick}
            aria-label='received-gift'
            data-gift-name={gift.giftItemName}
            data-gift-id={gift.id} >
            <Thumb src={gift.imageUrl} width={100} height={100} alt={gift.giftItemName} />
            <Info fullWidth spacing={2} align='flex-start'>
                <Text size='body-short' color={colors.textThird} fontWeight='regular'>{subtitle}</Text>
                <Name size='title' fontWeight='medium'>{gift.giftItemName}</Name>
                <Row fullWidth justify='flex-start' align='center' spacing={1}>
                    {gift.expiredAt && (
                        <Text size='body-long' color={colors.textSecondary}>
                            {t('giftBox.until', { date: formatGiftDate(gift.expiredAt) })}
                        </Text>
                    )}
                    {statusLabel && (
                        <Badge color={isUrgent ? 'red' : 'gray'}>{statusLabel}</Badge>
                    )}
                </Row>
            </Info>
        </Item>
    );
}

const Item = styled.button`
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${colors.primary};
        outline-offset: 2px;
    }
`;

const Thumb = styled(Image)`
    flex-shrink: 0;
    border-radius: 4px;
    object-fit: contain;
`;

const Info = styled(Column)`
    min-width: 0;
`;

const Name = styled(Text)`
    overflow-wrap: anywhere;
`;

export default ReceivedGiftItem;
